/**
 * LrBodyPrimitive — the linear-regression layer of the CLN candle type.
 *
 * CLN draws two synchronised layers: the real market candle (the chart's own
 * CandlestickSeries, keeping true OHLC) and a linear-regression BODY painted on
 * top of it by this primitive. Drawing the regression as a primitive rather
 * than as a candle transform is what lets the series keep real prices, so the
 * crosshair, OHLC legend, price scale, LTP line and P&L badge stay honest.
 *
 * Invariants this file must preserve:
 *   - The regression layer is ALWAYS body-only. There is no code path that
 *     draws an LR wick, in any Wick-toggle state. Regressing the real high and
 *     low separately would produce excursions that mean nothing.
 *   - Colour comes from LR direction alone (`up`), never from the real bar's
 *     own open/close direction.
 *   - Volume may tint the BORDER only. It never changes the hue, the fill
 *     alpha, or a single price coordinate.
 *
 * Modelled on RealOhlcPrimitive (same ISeriesPrimitive shape, bitmap-space
 * drawing, bar-spacing-derived width).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface LrBodyPoint {
  time: number;
  /** Regression open / close. Body spans these two; nothing else is drawn. */
  lrOpen: number;
  lrClose: number;
  /** LR direction — the sole colour input. */
  up: boolean;
  /** Thin participation on a directional body. Border accent only, and only
   *  when the caller has the accent enabled. */
  contradiction: boolean;
}

export interface LrBodyColors {
  up: string;
  down: string;
  /** Border drawn around a contradiction bar (accent is opt-in). */
  accent: string;
}

/** Fraction of one bar's spacing the regression body occupies. Narrower than
 *  the real candle behind it so both layers stay legible at normal zoom — but
 *  wide enough to read as a candle body rather than a tick. */
const WIDTH_RATIO = 0.64;
/** A doji-thin regression body still has to be visible, so floor its height. */
const MIN_BODY_PX = 1.25;

class LrBodyRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly points: LrBodyPoint[],
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
    private readonly colors: LrBodyColors,
    private readonly showAccent: boolean,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.points.length) return;
    const ts = this.chart.timeScale();
    const barSpacing = Number(ts.options().barSpacing) || 6;
    // Leave at least a hairline gap between adjacent bodies so a trend doesn't
    // fuse into one solid block.
    const halfW = Math.max(1, (barSpacing * WIDTH_RATIO) / 2);

    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      for (const p of this.points) {
        const x = ts.timeToCoordinate(p.time as Time);
        if (x == null) continue;
        const yO = this.series.priceToCoordinate(p.lrOpen);
        const yC = this.series.priceToCoordinate(p.lrClose);
        if (yO == null || yC == null) continue;

        const px = Number(x) * hpr;
        const left = Math.round(px - halfW * hpr);
        const right = Math.round(px + halfW * hpr);
        const w = Math.max(1, right - left);

        let top = Math.min(Number(yO), Number(yC)) * vpr;
        let bot = Math.max(Number(yO), Number(yC)) * vpr;
        const minH = MIN_BODY_PX * vpr;
        if (bot - top < minH) {
          const mid = (top + bot) / 2;
          top = mid - minH / 2;
          bot = mid + minH / 2;
        }

        ctx.fillStyle = p.up ? this.colors.up : this.colors.down;
        ctx.fillRect(left, Math.round(top), w, Math.max(1, Math.round(bot - top)));

        // Volume CONTRADICTION accent — a border, nothing more. The fill above
        // has already been painted at full strength in the directional hue, so
        // the bull/bear reading cannot be altered by participation.
        if (this.showAccent && p.contradiction) {
          ctx.strokeStyle = this.colors.accent;
          ctx.lineWidth = Math.max(1, Math.round(1.2 * vpr));
          ctx.strokeRect(left + 0.5, Math.round(top) + 0.5, w - 1, Math.max(1, Math.round(bot - top)) - 1);
        }
      }
      ctx.restore();
    });
  }
}

class LrBodyPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: LrBodyPrimitive) {}
  // Above the candles: the regression layer is the dominant read in CLN, and
  // the real candle behind it is drawn translucent to stay visible.
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const { chart, series, colors, points, showAccent } = this.parent;
    return chart && series ? new LrBodyRenderer(points, chart, series, colors, showAccent) : null;
  }
}

export class LrBodyPrimitive implements ISeriesPrimitive<Time> {
  points: LrBodyPoint[] = [];
  colors: LrBodyColors;
  showAccent = false;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: IPrimitivePaneView[];

  constructor(colors: LrBodyColors, points: LrBodyPoint[] = []) {
    this.colors = colors;
    this.points = points;
    this._paneViews = [new LrBodyPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }

  updateAllViews(): void { /* renderers read fresh from this.points */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(points: LrBodyPoint[]): void { this.points = points; this.chart?.applyOptions({}); }
  setColors(colors: LrBodyColors): void { this.colors = colors; this.chart?.applyOptions({}); }
  setShowAccent(on: boolean): void { this.showAccent = on; this.chart?.applyOptions({}); }
}
